import { useEffect, useId, useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TASK_NAME_MAX_LENGTH,
  TASK_PRIORITIES,
  type Task,
  type TaskPriority,
} from '@tailoring-crm/shared';
import { isApiRequestError } from '../../lib/api/errors.js';
import {
  useCreateTask,
  useUpdateTask,
  type ActivitySubject,
} from '../../lib/activity/use-activity.js';
import {
  offerableTaskCategories,
  useTaskCategories,
} from '../../lib/activity/use-task-categories.js';
import { dateInDays, followUpInstant } from '../../lib/follow-ups/follow-up-state.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Textarea,
  Label,
  useToast,
} from '@tailoring-crm/ui';

/**
 * "Ring them Thursday" — the one control that makes a follow-up for a customer. There is no
 * follow-up field on the record; this dialog is the only
 * way, and the server derives the record's next follow-up date from its open tasks.
 *
 * THE DUE DATE IS REQUIRED, and that is the point of the whole form. A task without a date would
 * sit on its record contributing nothing to the follow-up queue or the due counts —
 * invisible on the screens that exist to surface work. There is deliberately no "someday" option.
 *
 * Defaulted three days out, so the common case is two fields and a save.
 */

const DEFAULT_DAYS = 3;

/** `YYYY-MM-DD`, as `<input type="date">` needs it, from the instant the API stores. */
function dateInputValue(iso: string): string {
  const date = new Date(iso);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export interface TaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The customer the task is about. */
  subject: ActivitySubject;
  /** Present when editing; absent when making a new one. */
  task?: Task | null;
}

export function TaskDialog({
  open,
  onOpenChange,
  subject,
  task = null,
}: TaskDialogProps): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const fieldId = useId();

  const categories = useTaskCategories();
  const create = useCreateTask(subject);
  const update = useUpdateTask(subject);

  const [name, setName] = useState('');
  const [categoryKey, setCategoryKey] = useState('');
  const [dueOn, setDueOn] = useState(() => dateInDays(DEFAULT_DAYS));
  const [priority, setPriority] = useState<TaskPriority>('normal');
  const [description, setDescription] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(task?.name ?? '');
    setCategoryKey(task?.categoryKey ?? '');
    setDueOn(task ? dateInputValue(task.dueAt) : dateInDays(DEFAULT_DAYS));
    setPriority(task?.priority ?? 'normal');
    setDescription(task?.description ?? '');
    setFormError(null);
  }, [open, task]);

  const options = offerableTaskCategories(categories.data?.categories);
  const busy = create.isPending || update.isPending;

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    void (async () => {
      setFormError(null);
      const input = {
        name: name.trim(),
        categoryKey: categoryKey === '' ? null : categoryKey,
        // Stored as 9am on the chosen day, the same instant the follow-up date has always used:
        // a date pinned to midnight reads as overdue the moment the shop opens.
        dueAt: followUpInstant(dueOn),
        priority,
        description: description.trim() ? description.trim() : null,
      };

      try {
        if (task) await update.mutateAsync({ taskId: task.id, input });
        else await create.mutateAsync(input);
        toast({
          title: task ? t('activity.tasks.updated') : t('activity.tasks.added'),
          variant: 'success',
        });
        onOpenChange(false);
      } catch (error) {
        if (isApiRequestError(error)) {
          setFormError(error.message);
          return;
        }
        setFormError(error instanceof Error ? error.message : t('error.unexpected'));
      }
    })();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="atelier-dialog max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {task ? t('activity.tasks.editTitle') : t('activity.tasks.newTitle')}
          </DialogTitle>
          <DialogDescription>{t('activity.tasks.description')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          {formError ? (
            <p role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {formError}
            </p>
          ) : null}

          <div className="flex flex-col gap-2">
            <Label htmlFor={`${fieldId}-name`}>{t('activity.tasks.nameLabel')}</Label>
            <Input
              id={`${fieldId}-name`}
              value={name}
              maxLength={TASK_NAME_MAX_LENGTH}
              onChange={(event) => setName(event.target.value)}
              placeholder={t('activity.tasks.namePlaceholder')}
              required
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor={`${fieldId}-category`}>{t('activity.tasks.categoryLabel')}</Label>
              <select
                id={`${fieldId}-category`}
                value={categoryKey}
                onChange={(event) => setCategoryKey(event.target.value)}
                className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              >
                {/* A task may have no category at all — a shop that emptied the list still has a
                    working form, which is why nothing here is required. */}
                <option value="">{t('activity.tasks.categoryNone')}</option>
                {options.map((category) => (
                  <option key={category.key} value={category.key}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor={`${fieldId}-due`}>{t('activity.tasks.dueLabel')}</Label>
              <Input
                id={`${fieldId}-due`}
                type="date"
                value={dueOn}
                onChange={(event) => setDueOn(event.target.value)}
                required
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor={`${fieldId}-priority`}>{t('activity.tasks.priorityLabel')}</Label>
            <select
              id={`${fieldId}-priority`}
              value={priority}
              onChange={(event) => setPriority(event.target.value as TaskPriority)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              {TASK_PRIORITIES.map((one) => (
                <option key={one} value={one}>
                  {t(`activity.tasks.priority.${one}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor={`${fieldId}-description`}>{t('activity.tasks.notesLabel')}</Label>
            <Textarea
              id={`${fieldId}-description`}
              rows={3}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder={t('activity.tasks.notesPlaceholder')}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={busy || !name.trim() || !dueOn}>
              {task ? t('common.save') : t('activity.tasks.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
